import { useRevalidator } from "react-router";

import AssignmentApi from "@/api/assignment-api";
import UserApi from "@/api/user-api";
import ContextMenu, { type ContextMenuOption } from "@/components/context-menu";
import UserAvatar from "@/components/user-avatar";
import { copyTextToClipboard, boxclass, cn, handled } from "@/helpers";
import { useAuth } from "@/hooks/auth";
import useSnackbar from "@/hooks/snackbar";
import type { AssignmentUser as AssignmentUserType } from "@/types/assignment";

interface AssignmentUsersProps {
  assignment_users: AssignmentUserType[];
}

export default function AssignmentUsers({ assignment_users }: AssignmentUsersProps) {
  return (
    <div className="mx-auto w-96 space-y-4">
      {assignment_users.map((au) => {
        return <AssignmentUser key={au.email} user={au} />;
      })}
    </div>
  );
}

function AssignmentUser({ user }: { user: AssignmentUserType }) {
  const { getCreateUserLink } = useAuth();
  const { showSnackbar } = useSnackbar();
  const revalidator = useRevalidator();

  const role_title =
    user.role === "assessor"
      ? "Assessor"
      : user.role === "full_access"
        ? "Full access"
        : user.role === "owner"
          ? "Owner"
          : "Admin";

  function callbackFn() {
    void revalidator.revalidate();
  }

  async function _getCreateuserLink() {
    const { error, data } = await getCreateUserLink(user.email);
    if (error || !data) {
      console.error(error);
      showSnackbar("Failed to create an invitation link.", "warning");
      return;
    }
    void copyTextToClipboard(`${window.location.origin}/create-user/${data.token}`).then(
      (result) => {
        if (result) {
          showSnackbar("Invitation link copied!", "success");
        } else {
          showSnackbar("Failed to copy link.", "warning");
        }
      },
    );
  }

  async function _removeCompletely() {
    // TODO warn on existing data
    const { error } = await AssignmentApi.removeAssignmentUser(user.email, user.assignment_id);
    if (error) {
      console.error(error);
      showSnackbar(`Could not remove ${user.email}.`, "warning");
      return;
    }
    callbackFn();
  }

  async function _changeRole(new_role: string) {
    const { error } = await AssignmentApi.changeAssignmentUser(
      user.email,
      user.assignment_id,
      new_role,
    );
    if (error) {
      console.error(error);
      showSnackbar(`Could not change the role for ${user.email}.`, "warning");
      return;
    }
    callbackFn();
  }

  // Nothing on screen changes either way, so both outcomes need saying.
  async function _sendInvite() {
    const { error } = await UserApi.sendInvite(user.email, user.assignment_id);
    if (error) {
      console.error(error);
      showSnackbar(`Could not send an invite to ${user.email}.`, "warning");
      return;
    }
    showSnackbar(`Invite sent to ${user.email}.`, "success");
  }

  const context_options: ContextMenuOption[] = [];
  if (!user.account_created) {
    context_options.push({
      icon: "content_copy",
      label: "Copy invite URL",
      onClick: handled(_getCreateuserLink),
    });
    context_options.push({
      icon: "send",
      label: "Send invite",
      onClick: handled(_sendInvite),
    });
  }
  if (user.role === "assessor") {
    context_options.push({
      icon: "arrow_upward",
      label: "Give full access",
      onClick: handled(() => _changeRole("full_access")),
    });
  }
  if (user.role === "full_access") {
    context_options.push({
      icon: "arrow_downward",
      label: "Make assessor",
      onClick: handled(() => _changeRole("assessor")),
    });
  }
  if (user.role !== "owner") {
    context_options.push({
      icon: "delete",
      label: "Remove",
      onClick: handled(_removeCompletely),
    });
  }

  return (
    <div className={cn("grid grid-cols-[3.5rem_1fr_1rem] gap-4", boxclass)}>
      <UserAvatar size="medium" user={user} />
      <div className="flex flex-col">
        <div>
          {user.account_created ? (
            <span className="font-medium">{user.name}</span>
          ) : (
            <span className="text-slate-400">* Pending user creation</span>
          )}
        </div>
        <div className="text-sm text-slate-400">{user.email}</div>
        <div className="text-sm text-future-purple">{role_title}</div>
      </div>
      <ContextMenu options={context_options} />
    </div>
  );
}
